import { Card, Row, Col, Statistic, Typography, Spin, Empty, Progress, Tag, Divider } from "antd"
import { useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import axios from "axios"
import {
    FileTextOutlined,
    ClockCircleOutlined,
    ProjectOutlined,
    CalendarOutlined,
    BarChartOutlined,
    CheckCircleOutlined,
    UserDeleteOutlined,
    GiftOutlined,
    ThunderboltOutlined,
    CloseCircleOutlined
} from "@ant-design/icons"
import moment from "moment"

const { Title, Text } = Typography

const TimesheetDashboard = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [dashboardData, setDashboardData] = useState<any>({
        projectSummary: [],
        totalHours: 0,
        totalEntries: 0,
        totalLeave: 0,
        totalHolidays: 0,
        totalWeekendOvertime: 0,
        missedTimesheets: 0
    });
    const accessToken = searchParams.get('token') || localStorage.getItem('timesheet_access_token');

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (accessToken) {
            loadDashboardData();
        }
    }, [accessToken]);

    const loadDashboardData = async () => {
        if (!accessToken) return;

        setLoading(true);
        try {
            // Get current month start and end dates
            const startDate = moment().startOf('month').format('YYYY-MM-DD');
            const endDate = moment().endOf('month').format('YYYY-MM-DD');

            const params = new URLSearchParams({
                accessToken: accessToken,
                startDate: startDate,
                endDate: endDate
            });

            const response = await axios.get(`${process.env.API_URL}/timesheet/public/my-timesheets?${params.toString()}`);
            if (response.data.code === 200) {
                // Handle different response structures
                // The API returns: { code: 200, data: [...timesheets], pagination: {...} }
                const timesheets = Array.isArray(response.data.data)
                    ? response.data.data
                    : (response.data.data?.timesheets || []);

                // Calculate project-wise summary
                const projectMap: any = {};
                let totalHours = 0;

                timesheets.forEach((entry: any) => {
                    const projectName = entry.projectName || 'Unknown';
                    if (!projectMap[projectName]) {
                        projectMap[projectName] = {
                            projectName: projectName,
                            totalHours: 0,
                            entries: 0
                        };
                    }
                    projectMap[projectName].totalHours += entry.workedHours || 0;
                    projectMap[projectName].entries += 1;
                    totalHours += entry.workedHours || 0;
                });

                const projectSummary = Object.values(projectMap).sort((a: any, b: any) => b.totalHours - a.totalHours);

                // Load dashboard statistics
                const statsParams = new URLSearchParams({
                    accessToken: accessToken,
                    startDate: startDate,
                    endDate: endDate
                });

                const statsResponse = await axios.get(`${process.env.API_URL}/timesheet/public/dashboard-stats?${statsParams.toString()}`);

                if (statsResponse.data.code === 200) {
                    const stats = statsResponse.data.data || {};
                    setDashboardData({
                        projectSummary,
                        totalHours,
                        totalEntries: timesheets.length,
                        totalLeave: stats.totalLeave || 0,
                        totalHolidays: stats.totalHolidays || 0,
                        totalWeekendOvertime: stats.totalWeekendOvertime || 0,
                        missedTimesheets: stats.missedTimesheets || 0
                    });
                } else {
                    setDashboardData({
                        projectSummary,
                        totalHours,
                        totalEntries: timesheets.length,
                        totalLeave: 0,
                        totalHolidays: 0,
                        totalWeekendOvertime: 0,
                        missedTimesheets: 0
                    });
                }
            } else {
                console.error('Dashboard API error:', response.data);
            }
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!accessToken) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <Empty description="Please verify your email to view dashboard" />
            </div>
        );
    }

    // Calculate max hours for progress bars
    const maxHours = dashboardData.projectSummary.length > 0
        ? Math.max(...dashboardData.projectSummary.map((p: any) => p.totalHours))
        : 0;

    return (
        <div style={{
            padding: isMobile ? '16px 12px' : '24px 20px',
            background: '#f5f7fa',
            minHeight: '100vh'
        }}>
            {/* Header Section */}
            <div style={{
                marginBottom: isMobile ? 16 : 20,
                padding: isMobile ? '16px' : '20px',
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid #e8eaed'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                marginBottom: 4,
                                color: '#1a1a1a',
                                fontWeight: 600,
                                fontSize: isMobile ? '18px' : '20px'
                            }}
                        >
                            Timesheet Dashboard
                        </Title>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap'
                        }}>
                            <Text style={{
                                color: '#595959',
                                fontSize: isMobile ? '12px' : '13px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}>
                                <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '12px' }} />
                                {moment().format('MMMM YYYY')}
                            </Text>
                            <Divider type="vertical" style={{ margin: 0, height: 12 }} />
                            <Text style={{
                                color: '#8c8c8c',
                                fontSize: isMobile ? '11px' : '12px'
                            }}>
                                Summary of your timesheet entries
                            </Text>
                        </div>
                    </div>
                    {dashboardData.totalHours > 0 && (
                        <Tag
                            icon={<CheckCircleOutlined />}
                            color="success"
                            style={{
                                padding: '4px 10px',
                                fontSize: isMobile ? '11px' : '12px',
                                borderRadius: '4px',
                                fontWeight: 500,
                                border: 'none'
                            }}
                        >
                            Active Tracking
                        </Tag>
                    )}
                </div>
            </div>

            <Spin spinning={loading}>
                {/* Key Metrics Cards */}
                <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]} style={{ marginBottom: isMobile ? 16 : 20 }}>
                    {/* Existing cards for Total Hours, Total Entries, and Projects */}
                    <Col xs={24} sm={12} lg={8}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.12)',
                                border: 'none',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                transition: 'all 0.2s ease',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: isMobile ? '16px' : '20px', position: 'relative', zIndex: 1 }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.12)';
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: -15,
                                right: -15,
                                width: 80,
                                height: 80,
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />
                            <Statistic
                                title={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '11px' : '12px',
                                        fontWeight: 500,
                                        letterSpacing: '0.3px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Total Hours
                                    </span>
                                }
                                value={dashboardData.totalHours.toFixed(2)}
                                suffix={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: isMobile ? '14px' : '16px',
                                        fontWeight: 400
                                    }}>
                                        hrs
                                    </span>
                                }
                                prefix={
                                    <ClockCircleOutlined style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '18px' : '22px',
                                        marginRight: 6
                                    }} />
                                }
                                valueStyle={{
                                    color: '#fff',
                                    fontSize: isMobile ? '24px' : '28px',
                                    fontWeight: 600,
                                    lineHeight: 1.2
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(240, 147, 251, 0.12)',
                                border: 'none',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                transition: 'all 0.2s ease',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: isMobile ? '16px' : '20px', position: 'relative', zIndex: 1 }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(240, 147, 251, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(240, 147, 251, 0.12)';
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: -15,
                                right: -15,
                                width: 80,
                                height: 80,
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />
                            <Statistic
                                title={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '11px' : '12px',
                                        fontWeight: 500,
                                        letterSpacing: '0.3px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Total Entries
                                    </span>
                                }
                                value={dashboardData.totalEntries}
                                prefix={
                                    <FileTextOutlined style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '18px' : '22px',
                                        marginRight: 6
                                    }} />
                                }
                                valueStyle={{
                                    color: '#fff',
                                    fontSize: isMobile ? '24px' : '28px',
                                    fontWeight: 600,
                                    lineHeight: 1.2
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(79, 172, 254, 0.12)',
                                border: 'none',
                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                transition: 'all 0.2s ease',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: isMobile ? '16px' : '20px', position: 'relative', zIndex: 1 }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(79, 172, 254, 0.12)';
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: -15,
                                right: -15,
                                width: 80,
                                height: 80,
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />
                            <Statistic
                                title={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '11px' : '12px',
                                        fontWeight: 500,
                                        letterSpacing: '0.3px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Active Projects
                                    </span>
                                }
                                value={dashboardData.projectSummary.length}
                                prefix={
                                    <ProjectOutlined style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '18px' : '22px',
                                        marginRight: 6
                                    }} />
                                }
                                valueStyle={{
                                    color: '#fff',
                                    fontSize: isMobile ? '24px' : '28px',
                                    fontWeight: 600,
                                    lineHeight: 1.2
                                }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Additional Statistics Cards */}
                <Row gutter={[isMobile ? 16 : 24, isMobile ? 16 : 24]} style={{ marginBottom: isMobile ? 24 : 32 }}>
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(255, 107, 107, 0.15)',
                                border: 'none',
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: isMobile ? '20px' : '28px', position: 'relative', zIndex: 1 }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: -20,
                                right: -20,
                                width: 120,
                                height: 120,
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />
                            <Statistic
                                title={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '12px' : '13px',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Total Leave
                                    </span>
                                }
                                value={dashboardData.totalLeave}
                                suffix={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: isMobile ? '14px' : '16px',
                                        fontWeight: 500
                                    }}>
                                        days
                                    </span>
                                }
                                prefix={
                                    <UserDeleteOutlined style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '20px' : '28px',
                                        marginRight: 8
                                    }} />
                                }
                                valueStyle={{
                                    color: '#fff',
                                    fontSize: isMobile ? '28px' : '36px',
                                    fontWeight: 700,
                                    lineHeight: 1.2
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(255, 193, 7, 0.15)',
                                border: 'none',
                                background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: isMobile ? '20px' : '28px', position: 'relative', zIndex: 1 }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: -20,
                                right: -20,
                                width: 120,
                                height: 120,
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />
                            <Statistic
                                title={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '12px' : '13px',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Total Holidays
                                    </span>
                                }
                                value={dashboardData.totalHolidays}
                                suffix={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: isMobile ? '14px' : '16px',
                                        fontWeight: 500
                                    }}>
                                        days
                                    </span>
                                }
                                prefix={
                                    <GiftOutlined style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '20px' : '28px',
                                        marginRight: 8
                                    }} />
                                }
                                valueStyle={{
                                    color: '#fff',
                                    fontSize: isMobile ? '28px' : '36px',
                                    fontWeight: 700,
                                    lineHeight: 1.2
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)',
                                border: 'none',
                                background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: isMobile ? '20px' : '28px', position: 'relative', zIndex: 1 }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: -20,
                                right: -20,
                                width: 120,
                                height: 120,
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />
                            <Statistic
                                title={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '12px' : '13px',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Weekend Overtime
                                    </span>
                                }
                                value={dashboardData.totalWeekendOvertime.toFixed(1)}
                                suffix={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: isMobile ? '14px' : '16px',
                                        fontWeight: 500
                                    }}>
                                        hrs
                                    </span>
                                }
                                prefix={
                                    <ThunderboltOutlined style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '20px' : '28px',
                                        marginRight: 8
                                    }} />
                                }
                                valueStyle={{
                                    color: '#fff',
                                    fontSize: isMobile ? '28px' : '36px',
                                    fontWeight: 700,
                                    lineHeight: 1.2
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(244, 67, 54, 0.15)',
                                border: 'none',
                                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: isMobile ? '20px' : '28px', position: 'relative', zIndex: 1 }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: -20,
                                right: -20,
                                width: 120,
                                height: 120,
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />
                            <Statistic
                                title={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '12px' : '13px',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Missed Timesheets
                                    </span>
                                }
                                value={dashboardData.missedTimesheets}
                                suffix={
                                    <span style={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: isMobile ? '14px' : '16px',
                                        fontWeight: 500
                                    }}>
                                        days
                                    </span>
                                }
                                prefix={
                                    <CloseCircleOutlined style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        fontSize: isMobile ? '20px' : '28px',
                                        marginRight: 8
                                    }} />
                                }
                                valueStyle={{
                                    color: '#fff',
                                    fontSize: isMobile ? '28px' : '36px',
                                    fontWeight: 700,
                                    lineHeight: 1.2
                                }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Project Summary Section */}
                <Card
                    title={
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12
                        }}>
                            <BarChartOutlined style={{ fontSize: isMobile ? '18px' : '20px', color: '#1890ff' }} />
                            <span style={{
                                fontSize: isMobile ? '18px' : '20px',
                                fontWeight: 700,
                                color: '#1a1a1a',
                                letterSpacing: '-0.3px'
                            }}>
                                Project-wise Hours Summary
                            </span>
                        </div>
                    }
                    style={{
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e8eaed',
                        background: '#ffffff'
                    }}
                    bodyStyle={{ padding: isMobile ? '20px' : '32px' }}
                >
                    {dashboardData.projectSummary.length > 0 ? (
                        <Row gutter={[isMobile ? 16 : 20, isMobile ? 16 : 20]}>
                            {dashboardData.projectSummary.map((project: any, index: number) => {
                                const progressPercent = maxHours > 0 ? (project.totalHours / maxHours) * 100 : 0;
                                const colors = [
                                    ['#667eea', '#764ba2'],
                                    ['#f093fb', '#f5576c'],
                                    ['#4facfe', '#00f2fe'],
                                    ['#43e97b', '#38f9d7'],
                                    ['#fa709a', '#fee140'],
                                    ['#30cfd0', '#330867']
                                ];
                                const gradientColors = colors[index % colors.length];

                                return (
                                    <Col xs={24} sm={12} lg={8} key={index}>
                                        <Card
                                            hoverable
                                            style={{
                                                borderRadius: '14px',
                                                border: '1px solid #e8eaed',
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                background: '#ffffff',
                                                overflow: 'hidden'
                                            }}
                                            bodyStyle={{ padding: isMobile ? '20px' : '24px' }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: 16
                                            }}>
                                                <Text style={{
                                                    fontSize: isMobile ? '15px' : '16px',
                                                    color: '#1a1a1a',
                                                    fontWeight: 600,
                                                    lineHeight: 1.4
                                                }}>
                                                    {project.projectName}
                                                </Text>
                                                {index === 0 && (
                                                    <Tag
                                                        color="gold"
                                                        style={{
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            padding: '2px 8px',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        Top
                                                    </Tag>
                                                )}
                                            </div>
                                            <Statistic
                                                value={project.totalHours.toFixed(2)}
                                                suffix={
                                                    <span style={{
                                                        fontSize: isMobile ? '16px' : '18px',
                                                        color: '#8c8c8c',
                                                        fontWeight: 500
                                                    }}>
                                                        hrs
                                                    </span>
                                                }
                                                valueStyle={{
                                                    fontSize: isMobile ? '28px' : '36px',
                                                    fontWeight: 700,
                                                    color: '#1a1a1a',
                                                    lineHeight: 1.2,
                                                    marginBottom: 12
                                                }}
                                            />
                                            <Progress
                                                percent={Math.round(progressPercent)}
                                                strokeColor={{
                                                    '0%': gradientColors[0],
                                                    '100%': gradientColors[1]
                                                }}
                                                showInfo={false}
                                                strokeWidth={6}
                                                style={{ marginBottom: 12 }}
                                            />
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginTop: 8
                                            }}>
                                                <Tag
                                                    style={{
                                                        background: '#f5f7fa',
                                                        border: '1px solid #e8eaed',
                                                        borderRadius: '8px',
                                                        padding: '4px 12px',
                                                        fontSize: isMobile ? '12px' : '13px',
                                                        color: '#595959',
                                                        fontWeight: 500,
                                                        margin: 0
                                                    }}
                                                >
                                                    {project.entries} {project.entries === 1 ? 'entry' : 'entries'}
                                                </Tag>
                                                <Text style={{
                                                    fontSize: isMobile ? '12px' : '13px',
                                                    color: '#8c8c8c',
                                                    fontWeight: 500
                                                }}>
                                                    {progressPercent.toFixed(0)}% of max
                                                </Text>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <Text style={{
                                        color: '#8c8c8c',
                                        fontSize: isMobile ? '14px' : '16px',
                                        display: 'block',
                                        marginBottom: 8
                                    }}>
                                        No timesheet entries for this month
                                    </Text>
                                    <Text style={{
                                        color: '#bfbfbf',
                                        fontSize: isMobile ? '12px' : '14px'
                                    }}>
                                        Start tracking your time to see project summaries here
                                    </Text>
                                </div>
                            }
                            style={{ padding: '60px 0' }}
                        />
                    )}
                </Card>
            </Spin>
        </div>
    );
};

export default TimesheetDashboard;
